import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const validateEmail = (email) => {
  const regex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
};

export default function Contact(props) {
  useEffect(() => {
    document.title = props.title;
    if (window.location.hash === "#book") {
      document.getElementById("book").scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [props.title]);

  const form = useRef();
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [enteredName, setEnteredName] = useState("");
  const [enteredNameIsValid, setEnteredNameIsValid] = useState(false);
  const [enteredNameTouched, setEnteredNameTouched] = useState(false);

  const [enteredEmail, setEnteredEmail] = useState("");
  const [enteredEmailIsValid, setEnteredEmailIsValid] = useState(false);
  const [enteredEmailTouched, setEnteredEmailTouched] = useState(false);

  const [enteredPhone, setEnteredPhone] = useState("");
  const [enteredPhoneIsValid, setEnteredPhoneIsValid] = useState(false);
  const [enteredPhoneTouched, setEnteredPhoneTouched] = useState(false);

  const [enteredMessage, setEnteredMessage] = useState("");

  const changeNameHandler = (e) => {
    setEnteredName(e.target.value);
    if (e.target.value.trim() !== "") {
      setEnteredNameIsValid(true);
    }
  };

  const nameInputBlurHandler = () => {
    setEnteredNameTouched(true);
    if (enteredName.trim() === "") {
      setEnteredNameIsValid(false);
    } else {
      setEnteredNameIsValid(true);
    }
  };

  const changeEmailHandler = (e) => {
    setEnteredEmail(e.target.value);
    if (validateEmail(e.target.value.trim())) {
      setEnteredEmailIsValid(true);
    }
  };

  const emailInputBlurHandler = () => {
    setEnteredEmailTouched(true);
    if (enteredEmail.trim() === "") {
      setEnteredEmailIsValid(false);
    } else {
      setEnteredEmailIsValid(true);
    }
  };

  const changePhoneHandler = (e) => {
    setEnteredPhone(e.target.value);
    if (e.target.value.trim() !== "") {
      setEnteredPhoneIsValid(true);
    }
  };

  const phoneInputBlurHandler = () => {
    setEnteredPhoneTouched(true);
    if (enteredPhone.trim() === "") {
      setEnteredPhoneIsValid(false);
    } else {
      setEnteredPhoneIsValid(true);
    }
  };

  const onSubmit = (data) => {
    return fetch("https://backend.auth.easyhiring.in/home/book-demo", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .catch((err) => console.log(err));
  };

  const submit = (e) => {
    e.preventDefault();

    setMessage("");
    setEnteredNameTouched(true);
    setEnteredEmailTouched(true);
    setEnteredPhoneTouched(true);

    if (enteredName.trim() === "") {
      setEnteredNameIsValid(false);
      return;
    }
    if (enteredEmail.trim() === "") {
      setEnteredEmailIsValid(false);
      return;
    }
    if (enteredPhone.trim() === "") {
      setEnteredPhoneIsValid(false);
      return;
    }

    setLoading(true);

    onSubmit({ name: enteredName, email: enteredEmail, phone: enteredPhone, message: enteredMessage })
      .then((res) => {
        setMessage(res.message);
        setSuccess(true);
        setEnteredName("");
        setEnteredEmail("");
        setEnteredPhone("");
        setEnteredMessage("");
      })
      .catch(() => {
        setSuccess(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const faqs = [
    {
      question: "What services does Easy Hiring provide?",
      answer: "Easy Hiring provides IT Services and Talent Outsourcing, including Staff Augmentation, Dedicated Teams, Project Outsourcing, and Managed Services. We deploy pre-trained engineers to work on your projects while they remain on our payroll."
    },
    {
      question: "How does the engagement model work?",
      answer: "We offer flexible engagement models. You can hire individual engineers for staff augmentation, build dedicated teams for your projects, outsource complete projects to us, or opt for managed services with ongoing support."
    },
    {
      question: "What technologies do your engineers specialize in?",
      answer: "Our engineers are trained in AI/ML, Full Stack Development (React, Angular, Node.js, Python), Data Engineering, Cloud Technologies (AWS, Azure, GCP), Automation Testing, Salesforce, and more."
    },
    {
      question: "How quickly can you deploy engineers?",
      answer: "We maintain a ready talent pipeline and can typically deploy engineers within 1-2 weeks of finalizing requirements. For urgent needs, we may be able to expedite the process."
    }
  ];

  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="bg-[#f9f8f8]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 gradient-hero">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full border-2 border-[#614a4433] text-sm font-medium text-[#453f3d] mb-6">
                <span className="w-2 h-2 bg-[#156cc2] rounded-full"></span>
                Contact Us
              </span>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1a1615] font-display tracking-tight leading-tight mb-6">
                Let's build your team together
              </h1>
              <p className="text-lg text-[#453f3d] leading-relaxed">
                Ready to scale your engineering capacity? Get in touch with us and let's discuss how we can help.
              </p>
            </div>
            <div className="hidden lg:block">
              <img
                className="w-full max-w-md mx-auto"
                src="assets/img/12982910_5124556.png"
                alt="Contact Us"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20" id="book">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-[20px] border-2 border-[#614a4433] shadow-[0_4px_50px_#614a440f] p-6">
                <div className="w-12 h-12 rounded-full bg-[#156cc2]/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#156cc2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-[#1a1615] mb-2">Office Address</h3>
                <a
                  href="https://goo.gl/maps/gu8pcd1Db11H2JvY8"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#453f3d] hover:text-[#156cc2] transition-colors"
                >
                  GVR Infra Rd Number 12,<br />
                  Ayyappa Society, Madhapur<br />
                  Hyderabad, Telangana
                </a>
              </div>

              <div className="bg-white rounded-[20px] border-2 border-[#614a4433] shadow-[0_4px_50px_#614a440f] p-6">
                <div className="w-12 h-12 rounded-full bg-[#cf8d13]/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#cf8d13]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-[#1a1615] mb-2">Email Us</h3>
                <a
                  href="mailto:info@easyhiring.in"
                  className="text-[#156cc2] hover:underline"
                >
                  info@easyhiring.in
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-[20px] border-2 border-[#614a4433] shadow-[0_4px_50px_#614a440f] p-8">
                <h2 className="text-2xl font-bold text-[#1a1615] mb-6">Send us a message</h2>

                {message && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-[12px] text-green-800">
                    {message}
                  </div>
                )}

                <form ref={form} onSubmit={submit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#453f3d] mb-2">Name</label>
                      <input
                        type="text"
                        name="user_name"
                        value={enteredName}
                        onChange={changeNameHandler}
                        onBlur={nameInputBlurHandler}
                        placeholder="Enter your name"
                        className={`w-full px-4 py-3 rounded-[12px] border-2 ${
                          enteredNameTouched && !enteredNameIsValid
                            ? "border-red-300 focus:border-red-500"
                            : "border-[#614a4433] focus:border-[#156cc2]"
                        } bg-white text-[#1a1615] placeholder-[#7a7270] outline-none transition-colors`}
                        required
                      />
                      {enteredNameTouched && !enteredNameIsValid && (
                        <p className="text-red-500 text-sm mt-1">Please enter your name</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#453f3d] mb-2">Email</label>
                      <input
                        type="email"
                        name="user_email"
                        value={enteredEmail}
                        onChange={changeEmailHandler}
                        onBlur={emailInputBlurHandler}
                        placeholder="Enter your email"
                        className={`w-full px-4 py-3 rounded-[12px] border-2 ${
                          enteredEmailTouched && !enteredEmailIsValid
                            ? "border-red-300 focus:border-red-500"
                            : "border-[#614a4433] focus:border-[#156cc2]"
                        } bg-white text-[#1a1615] placeholder-[#7a7270] outline-none transition-colors`}
                        required
                      />
                      {enteredEmailTouched && !enteredEmailIsValid && (
                        <p className="text-red-500 text-sm mt-1">Please enter a valid email</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#453f3d] mb-2">Phone</label>
                    <input
                      type="tel"
                      name="user_phone"
                      value={enteredPhone}
                      onChange={changePhoneHandler}
                      onBlur={phoneInputBlurHandler}
                      placeholder="Enter your phone number"
                      pattern="[0-9]{10}"
                      className={`w-full px-4 py-3 rounded-[12px] border-2 ${
                        enteredPhoneTouched && !enteredPhoneIsValid
                          ? "border-red-300 focus:border-red-500"
                          : "border-[#614a4433] focus:border-[#156cc2]"
                      } bg-white text-[#1a1615] placeholder-[#7a7270] outline-none transition-colors`}
                      required
                    />
                    {enteredPhoneTouched && !enteredPhoneIsValid && (
                      <p className="text-red-500 text-sm mt-1">Please enter your phone number</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#453f3d] mb-2">Message</label>
                    <textarea
                      name="message"
                      value={enteredMessage}
                      onChange={(e) => setEnteredMessage(e.target.value)}
                      placeholder="Tell us about your requirements..."
                      rows={4}
                      className="w-full px-4 py-3 rounded-[12px] border-2 border-[#614a4433] focus:border-[#156cc2] bg-white text-[#1a1615] placeholder-[#7a7270] outline-none transition-colors resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full md:w-auto px-8 py-4 bg-[#156cc2] text-white font-semibold rounded-full hover:bg-[#1259a8] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#f4f1ee]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border-2 border-[#614a4433] text-sm font-medium text-[#453f3d] mb-6">
                <span className="w-2 h-2 bg-[#cf8d13] rounded-full"></span>
                FAQs
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1a1615] font-display tracking-tight mb-6">
                Frequently asked questions
              </h2>
              <p className="text-[#453f3d] leading-relaxed mb-8">
                Can't find what you're looking for? Feel free to reach out to us directly.
              </p>
              <img
                className="w-full max-w-sm hidden lg:block"
                src="assets/img/urban-208.gif"
                alt="FAQ"
              />
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-[16px] border-2 border-[#614a4433] overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left"
                  >
                    <span className="font-semibold text-[#1a1615]">{faq.question}</span>
                    <svg
                      className={`w-5 h-5 text-[#156cc2] transition-transform ${openFaq === index ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-4">
                          <p className="text-[#453f3d] leading-relaxed">{faq.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
